/**
 * useLeadsWithSuburbs — composes the leads stream with Phase 4 region matching
 * to classify each lead into one of two pools:
 *
 *   - `lead_to_book` — address resolves to a zone whose verdict falls in the
 *     APPROVED bucket. These are book-worthy: in service area, good verdict.
 *   - `lead`         — everything else (no address, no zone match, or a
 *     review/outside verdict). The remarketing pool.
 *
 * Same incremental-resolution shape as `useDealsWithSuburbs`: leads return
 * instantly from the adapter cache, region lookups run batched in the
 * background, and consumers can render a lead row before its region resolves.
 *
 * Leads carry no upstream coordinates (Contact has no geocode fields), so
 * region matching runs on the address string alone — the suburb-exact and
 * postcode paths in `findRegionForAddress` cover that case.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLeads } from './useLeads';
import { useTenant } from '../contexts/TenantContext';
import { findRegionForAddress, verdictBucket, type RegionMatch } from '../utils/regionValidation';
import type { Contact } from '../lib/crm/types';

export type LeadStatus = 'lead' | 'lead_to_book';

export interface LeadWithSuburb {
  lead: Contact;
  regionMatch: RegionMatch | null;
  leadStatus: LeadStatus;
  loadingRegion: boolean;
}

interface UseLeadsWithSuburbsReturn {
  items: LeadWithSuburb[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * A lead is "to book" when its resolved region sits in a zone AND that zone's
 * verdict is in the approved bucket. No zone, no verdict, or a review/outside
 * verdict all stay in the plain remarketing pool.
 */
export function classifyLead(regionMatch: RegionMatch | null): LeadStatus {
  if (!regionMatch?.zone) return 'lead';
  return verdictBucket(regionMatch.verdict?.label) === 'approved' ? 'lead_to_book' : 'lead';
}

export function useLeadsWithSuburbs(): UseLeadsWithSuburbsReturn {
  const { tenant } = useTenant();
  const { leads, loading, error, refetch } = useLeads();

  const [regionByLead, setRegionByLead] = useState<Map<string, RegionMatch>>(() => new Map());
  const [resolving, setResolving] = useState<Set<string>>(() => new Set());

  // Cancel in-flight resolves on unmount so we don't setState on a dead tree.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Trigger background resolution for any lead we haven't seen yet.
  useEffect(() => {
    if (!tenant) return;
    const todo = leads.filter((l) => !regionByLead.has(l.id) && !resolving.has(l.id));
    if (todo.length === 0) return;

    setResolving((prev) => {
      const next = new Set(prev);
      for (const l of todo) next.add(l.id);
      return next;
    });

    // Resolve in parallel but batched to avoid hammering Supabase.
    const BATCH = 8;
    void (async () => {
      for (let i = 0; i < todo.length; i += BATCH) {
        const slice = todo.slice(i, i + BATCH);
        const results = await Promise.all(
          slice.map(async (l) => {
            const region = await findRegionForAddress(l.address ?? '');
            return [l.id, region] as const;
          }),
        );
        if (!aliveRef.current) return;
        setRegionByLead((prev) => {
          const next = new Map(prev);
          for (const [id, region] of results) next.set(id, region);
          return next;
        });
        setResolving((prev) => {
          const next = new Set(prev);
          for (const [id] of results) next.delete(id);
          return next;
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, tenant]);

  const items = useMemo<LeadWithSuburb[]>(() => {
    return leads.map((lead) => {
      const region = regionByLead.get(lead.id) ?? null;
      return {
        lead,
        regionMatch: region,
        leadStatus: classifyLead(region),
        loadingRegion: resolving.has(lead.id),
      };
    });
  }, [leads, regionByLead, resolving]);

  return {
    items,
    loading,
    error: error?.message ?? null,
    refetch,
  };
}
